import { getHeaders, getUserDetails } from 'auth/auth';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';

export const documentAPI = createApi({
  reducerPath: 'Document',
  tagTypes: ['updateList', 'docGenTemplates', 'signatureStatus', 'documentNotes'],
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    // Legacy aggregate endpoint used for B2B flows
    getDocumentList: builder.query({
      query: ({ applicationId, borrowerId } = {}) => {
        const qs = borrowerId ? `?borrower_id=${borrowerId}` : '';
        return {
          url: `/b2b/documents/aggregate/loan/${applicationId}${qs}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['updateList'],
    }),
    // B2C v2 endpoint using aggregate-style grouped payload under `documents`
    getB2cDocumentList: builder.query({
      query: ({ applicationId, borrowerId } = {}) => {
        const params = {};
        if (applicationId) params.applicationId = applicationId;
        if (borrowerId) params.borrowerId = borrowerId;
        return {
          url: `/b2c/loan/documents`,
          method: 'GET',
          params,
          headers: getHeaders(),
        };
      },
      transformResponse: (response) => ({
        documents: response?.documents ?? [],
        total: Array.isArray(response?.documents)
          ? response.documents.length
          : 0,
        skip: 0,
        limit: Array.isArray(response?.documents)
          ? response.documents.length
          : 0,
      }),
      providesTags: ['updateList'],
      keepUnusedDataFor: 300, // 5 minutes cache persistence
    }),
    getUserProperty: builder.query({
      query: () => {
        return {
          url: `/b2b/documents/user_info`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    deleteDocument: builder.mutation({
      query: ({ documentId }) => {
        const headers = getHeaders();
        delete headers.accept;
        headers.Accept = 'application/json';
        return {
          url: `/b2b/documents/${documentId}`,
          method: 'DELETE',
          headers,
        };
      },
      invalidatesTags: ['updateList'],
    }),
    deleteB2cDocument: builder.mutation({
      query: ({ documentId }) => {
        const headers = getHeaders();
        delete headers.accept;
        headers.Accept = 'application/json';
        return {
          url: `/b2c/loan/documents/${documentId}`,
          method: 'DELETE',
          headers,
        };
      },
      invalidatesTags: ['updateList'],
    }),
    getDocumentById: builder.query({
      query: ({ documentId }) => ({
        url: `/b2b/documents/get`,
        method: 'GET',
        params: { document_id: documentId },
        headers: getHeaders(),
      }),
    }),
    getB2cDocumentById: builder.query({
      query: ({ documentId }) => ({
        url: `/b2c/documents/get`,
        method: 'GET',
        params: { document_id: documentId },
        headers: getHeaders(),
      }),
    }),
    uploadPredefinedDocs: builder.mutation({
      query: ({ data, additionalParams }) => {
        const formData = new FormData();
        formData.append('file', data);
        Object.keys(additionalParams || {}).forEach((key) => {
          if (
            additionalParams[key] !== undefined &&
            additionalParams[key] !== null &&
            additionalParams[key] !== ''
          ) {
            formData.append(key, additionalParams[key]);
          }
        });
        const headers = getHeaders();
        delete headers['Content-Type'];
        delete headers['content-type'];
        return {
          url: `/b2b/documents/upload`,
          method: 'POST',
          headers,
          body: formData,
        };
      },
      invalidatesTags: ['updateList'],
    }),
    uploadB2cDocument: builder.mutation({
      query: ({ data, additionalParams }) => {
        const formData = new FormData();
        formData.append('file', data);
        const {
          document_category_id,
          document_type_id,
          documentTypeCode,
          documentTitle,
          title,
          applicationId,
          borrowerId,
          tags,
          ...remainingParams
        } = additionalParams || {};

        const appendedKeys = new Set();
        const appendIfDefined = (key, value) => {
          if (value !== undefined && value !== null && value !== '') {
            formData.append(key, value);
            appendedKeys.add(key);
          }
        };

        appendIfDefined('document_category_id', document_category_id);
        appendIfDefined('document_type_id', document_type_id);
        appendIfDefined('document_type_code', documentTypeCode);
        appendIfDefined('title', documentTitle || title);
        appendIfDefined('applicationId', applicationId);
        appendIfDefined('borrowerId', borrowerId);

        if (Array.isArray(tags) && tags.length > 0) {
          appendIfDefined('tags', tags.join(','));
        }

        Object.entries(remainingParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null && !appendedKeys.has(key)) {
            formData.append(key, value);
          }
        });

        const headers = getHeaders();
        delete headers['Content-Type'];
        delete headers['content-type'];
        return {
          url: `/b2c/loan/documents/upload`,
          method: 'POST',
          headers,
          body: formData,
        };
      },
      invalidatesTags: ['updateList'],
    }),
    updatePredefinedDocs: builder.mutation({
      query: ({ data, additionalParams, documentId }) => {
        const formData = new FormData();
        formData.append('file', data);
        Object.keys(additionalParams || {}).forEach((key) => {
          if (
            additionalParams[key] !== undefined &&
            additionalParams[key] !== null &&
            additionalParams[key] !== ''
          ) {
            formData.append(key, additionalParams[key]);
          }
        });
        return {
          url: `/b2b/documents/update_document_file/${documentId}`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['updateList'],
    }),
    readFile: builder.mutation({
      query: ({ document_id }) => ({
        url: `/b2b/documents/download`,
        method: 'GET',
        params: { document_id },
        headers: getHeaders(),
        responseHandler: (response) => response.blob(),
      }),
    }),
    // ------------------------------------------------------------------
    // Document Generation & e-Signature (PROD-206)
    // ------------------------------------------------------------------
    getDocumentTemplates: builder.query({
      query: ({ documentScope } = {}) => {
        const params = {};
        if (documentScope) params.document_scope = documentScope;
        return {
          url: `/b2b/documents/templates`,
          method: 'GET',
          params,
          headers: getHeaders(),
        };
      },
      providesTags: ['docGenTemplates'],
      keepUnusedDataFor: 300,
    }),
    getDocumentTemplateById: builder.query({
      query: ({ templateId }) => ({
        url: `/b2b/documents/templates/${templateId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),
    requestSignature: builder.mutation({
      query: (payload) => ({
        url: `/b2b/documents/signatures/request`,
        method: 'POST',
        headers: getHeaders(),
        body: payload,
      }),
      invalidatesTags: ['signatureStatus', 'updateList'],
    }),
    getApplicationConditionDocuments: builder.query({
      query: ({ applicationId }) => ({
        url: `/b2b/documents/signatures/conditions/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: (result, error, arg) =>
        arg?.applicationId
          ? [{ type: 'signatureStatus', id: arg.applicationId }]
          : ['signatureStatus'],
    }),
    // Outstanding-document reminder (doc-gen UI v2): preview shows which
    // borrowers/documents would be reminded; send fires the notification.
    getDocumentReminderPreview: builder.query({
      query: ({ applicationId }) => ({
        url: `/b2b/documents/reminder-preview?loan_application_id=${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),
    // Read-only automatic-reminder schedule (cadence + restricted dates);
    // LOS-owned when FUNDMORE_REMINDER_CONFIG_SOURCE=los, POS-local otherwise.
    getReminderSchedule: builder.query({
      query: () => ({
        url: `/b2b/documents/reminder-schedule`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),
    sendDocumentReminder: builder.mutation({
      query: ({ applicationId, comment, sendOnBehalfOfPrimaryMs = true }) => ({
        url: `/b2b/documents/send-reminder`,
        method: 'POST',
        headers: getHeaders(),
        body: {
          loan_application_id: applicationId,
          send_on_behalf_of_primary_ms: sendOnBehalfOfPrimaryMs,
          ...(comment ? { comment } : {}),
        },
      }),
    }),
    // Send Documents to borrowers (PROD-400/401/402): preview feeds the
    // sidebar (documents with upload time + recipients with valid-email
    // flag); send delivers + notifies and flips isShared on the aggregate.
    getDocumentSendPreview: builder.query({
      query: ({ applicationId }) => ({
        url: `/b2b/documents/send-preview?loan_application_id=${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),
    sendDocumentsToBorrowers: builder.mutation({
      query: ({ applicationId, documentIds, recipientBorrowerIds, notes }) => ({
        url: `/b2b/documents/send`,
        method: 'POST',
        headers: getHeaders(),
        body: {
          loan_application_id: applicationId,
          document_ids: documentIds,
          recipient_borrower_ids: recipientBorrowerIds,
          ...(notes ? { notes } : {}),
        },
      }),
      // Refresh the documents aggregate so Shared badges appear.
      invalidatesTags: ['updateList'],
    }),
    getSignedDocumentContentUrl: builder.query({
      query: ({ applicationDocumentId }) => ({
        url: `/b2b/documents/signatures/${applicationDocumentId}/content-url`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),
    getB2bRecipientView: builder.query({
      query: ({ documentRequestId, stakeholderId }) => ({
        url: `/b2b/documents/signatures/recipient-view/${documentRequestId}/${stakeholderId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),
    markSignatureObserved: builder.mutation({
      query: ({ applicationDocumentId }) => ({
        url: `/b2b/documents/signatures/${applicationDocumentId}/observed-signed`,
        method: 'POST',
        headers: getHeaders(),
      }),
      invalidatesTags: ['signatureStatus'],
    }),
    // Pull LOS-side condition documents into our local `documents` table
    // (called on fm:document-saved + at the start of request_signature).
    // Server-side does the diff + download + insert; response is just
    // counters + errors.
    syncDocumentsFromLos: builder.mutation({
      query: ({ applicationId, fundmoreApplicationId } = {}) => ({
        url: `/b2b/documents/sync-from-los`,
        method: 'POST',
        headers: getHeaders(),
        body: {
          application_id: applicationId,
          ...(fundmoreApplicationId
            ? { fundmore_application_id: fundmoreApplicationId }
            : {}),
        },
      }),
      invalidatesTags: ['signatureStatus', 'updateList'],
    }),
    // B2C borrower signature endpoints
    getB2cApplicationConditionDocuments: builder.query({
      query: ({ applicationId, stakeholderId } = {}) => {
        const params = {};
        if (stakeholderId) params.stakeholder_id = stakeholderId;
        return {
          url: `/b2c/loan/documents/signatures/conditions/${applicationId}`,
          method: 'GET',
          params,
          headers: getHeaders(),
        };
      },
      providesTags: (result, error, arg) =>
        arg?.applicationId
          ? [{ type: 'signatureStatus', id: `b2c:${arg.applicationId}` }]
          : ['signatureStatus'],
    }),
    getB2cRecipientView: builder.query({
      query: ({ documentRequestId, stakeholderId }) => ({
        url: `/b2c/loan/documents/signatures/recipient-view/${documentRequestId}/${stakeholderId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),
    getB2cSignedDocumentContentUrl: builder.query({
      query: ({ applicationDocumentId }) => ({
        url: `/b2c/loan/documents/signatures/${applicationDocumentId}/content-url`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),
    // --- Per-document chat-style notes (B2C side). Borrower-side mirror of
    //     the admin document_notes API. `borrowerId` disambiguates primary
    //     vs co-borrower when they share a login. ---
    listDocumentNotes: builder.query({
      query: ({ documentId }) => ({
        url: `/b2c/documents/documents/${documentId}/notes`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: (_r, _e, { documentId }) => [
        { type: 'documentNotes', id: documentId },
      ],
    }),
    appendDocumentNote: builder.mutation({
      query: ({ documentId, body, borrowerId }) => {
        const qs = borrowerId ? `?borrower_id=${borrowerId}` : '';
        return {
          url: `/b2c/documents/documents/${documentId}/notes${qs}`,
          method: 'POST',
          headers: getHeaders(),
          body,
        };
      },
      invalidatesTags: (_r, _e, { documentId }) => [
        { type: 'documentNotes', id: documentId },
      ],
    }),
    markDocumentNoteRead: builder.mutation({
      query: ({ documentId, noteId, borrowerId }) => {
        const qs = borrowerId ? `?borrower_id=${borrowerId}` : '';
        return {
          url: `/b2c/documents/documents/${documentId}/notes/${noteId}/read${qs}`,
          method: 'PATCH',
          headers: getHeaders(),
        };
      },
      invalidatesTags: (_r, _e, { documentId }) => [
        { type: 'documentNotes', id: documentId },
      ],
    }),
    deleteDocumentNote: builder.mutation({
      query: ({ documentId, noteId, borrowerId }) => {
        const qs = borrowerId ? `?borrower_id=${borrowerId}` : '';
        return {
          url: `/b2c/documents/documents/${documentId}/notes/${noteId}${qs}`,
          method: 'DELETE',
          headers: getHeaders(),
        };
      },
      invalidatesTags: (_r, _e, { documentId }) => [
        { type: 'documentNotes', id: documentId },
      ],
    }),
  }),
});

// api for view document is present in hooks > pdfViewer.js

export const {
  useGetDocumentListQuery,
  useGetB2cDocumentListQuery,
  useGetDocumentByIdQuery,
  useGetB2cDocumentByIdQuery,
  useLazyGetDocumentByIdQuery,
  useLazyGetB2cDocumentByIdQuery,
  useUploadPredefinedDocsMutation,
  useUploadB2cDocumentMutation,
  useUpdatePredefinedDocsMutation,
  useGetUserPropertyQuery,
  useReadFileMutation,
  useDeleteDocumentMutation,
  useDeleteB2cDocumentMutation,
  useGetDocumentTemplatesQuery,
  useLazyGetDocumentTemplateByIdQuery,
  useRequestSignatureMutation,
  useGetApplicationConditionDocumentsQuery,
  useLazyGetDocumentReminderPreviewQuery,
  useLazyGetReminderScheduleQuery,
  useSendDocumentReminderMutation,
  useLazyGetDocumentSendPreviewQuery,
  useSendDocumentsToBorrowersMutation,
  useLazyGetSignedDocumentContentUrlQuery,
  useLazyGetB2bRecipientViewQuery,
  useMarkSignatureObservedMutation,
  useSyncDocumentsFromLosMutation,
  useGetB2cApplicationConditionDocumentsQuery,
  useLazyGetB2cRecipientViewQuery,
  useLazyGetB2cSignedDocumentContentUrlQuery,
  useListDocumentNotesQuery,
  useAppendDocumentNoteMutation,
  useMarkDocumentNoteReadMutation,
  useDeleteDocumentNoteMutation,
} = documentAPI;
